import { useEffect, useState, useCallback } from "react";
import { MapPin, LogIn, LogOut, Loader2, Clock, Users, WifiOff } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { getQueue } from "../lib/offline";
import { AttendanceHistory } from "./AttendanceHistory";
import { fmtH } from "../lib/format";

const LOCAL_KEY = "attendance_local_v1";
const readLocal = () => { try { return JSON.parse(localStorage.getItem(LOCAL_KEY) || "[]"); } catch { return []; } };
const writeLocal = (l) => localStorage.setItem(LOCAL_KEY, JSON.stringify(l));

const getGps = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({});
    navigator.geolocation.getCurrentPosition(
        (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => resolve({}),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
});
export const hhmm = (iso) => new Date(iso).toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" });
export const elapsed = (iso) => { const m = Math.max(0, Math.floor((Date.now() - new Date(iso)) / 60000)); return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")} min`; };

function useTicker(active) {
    const [, setT] = useState(0);
    useEffect(() => { if (!active) return; const i = setInterval(() => setT((x) => x + 1), 30000); return () => clearInterval(i); }, [active]);
}

export function Attendance({ jobId, jobTitle, jobs, onSaved, solo = false }) {
    const [open, setOpen] = useState([]);
    const [elsewhere, setElsewhere] = useState({});
    const [local, setLocal] = useState(readLocal());
    const [cards, setCards] = useState([]);
    const [picked, setPicked] = useState([]);
    const [job, setJob] = useState(jobId || "");
    const [busy, setBusy] = useState(false);
    const [histKey, setHistKey] = useState(0);

    const load = useCallback(() => {
        if (!job) { setOpen([]); setElsewhere({}); return; }
        api.get("/attendance/current", { params: { job_id: job } }).then((r) => {
            setOpen(r.data.open || []); setElsewhere(r.data.elsewhere || {});
            if (getQueue().length === 0) { writeLocal([]); setLocal([]); }
        }).catch(() => {});
    }, [job]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { api.get("/craftsmen/cards", { params: job ? { job_id: job } : {} }).then((r) => setCards(r.data.filter((c) => c.is_active !== false))).catch(() => {}); }, [job]);
    useEffect(() => { if (jobId) setJob(jobId); }, [jobId]);
    useEffect(() => { const h = () => load(); window.addEventListener("offline-queue-changed", h); return () => window.removeEventListener("offline-queue-changed", h); }, [load]);

    const present = [...open.map((r) => ({ id: r.id, craftsman_id: r.craftsman_id, name: r.craftsman_name, at: r.check_in.at, gps: r.check_in.lat })),
        ...local.filter((l) => l.job_id === job && !open.some((r) => r.craftsman_id === l.craftsman_id)).map((l) => ({ ...l, offline: true }))];
    const presentIds = new Set(present.map((p) => p.craftsman_id));
    const others = solo ? [] : cards.filter((c) => !presentIds.has(c.id));
    useTicker(present.length > 0);

    const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

    const checkIn = async () => {
        if (!job) { toast.error("Vyberte zakázku"); return; }
        if (!solo && !picked.length) { toast.error("Zaškrtněte, kdo přišel na stavbu"); return; }
        setBusy(true);
        try {
            const gps = await getGps();
            const at = new Date().toISOString();
            const { data } = await api.post("/attendance/check-in", { job_id: job, craftsman_ids: picked, at, ...gps });
            if (data._offline) {
                const mine = solo ? [{ craftsman_id: "me", name: "Vy" }] : cards.filter((c) => picked.includes(c.id)).map((c) => ({ craftsman_id: c.id, name: c.name }));
                const l = [...readLocal(), ...mine.map((m) => ({ ...m, job_id: job, at }))]; writeLocal(l); setLocal(l);
                toast.info(`Příchod ${hhmm(at)} uložen offline — odešle se po připojení`);
            } else {
                toast.success(`Příchod ${hhmm(data.at)} zapsán${gps.lat ? " (GPS)" : " (bez GPS)"}${!solo ? ` — ${data.created.length} ${data.created.length === 1 ? "osoba" : "osoby"}` : ""}`);
                load();
            }
            setPicked([]);
        } catch (e) { toast.error(e.response?.data?.detail || "Příchod se nepodařilo zapsat"); }
        setBusy(false);
    };

    const checkOut = async (ids) => {
        setBusy(true);
        try {
            const gps = await getGps();
            const at = new Date().toISOString();
            const { data } = await api.post("/attendance/check-out", { job_id: job, craftsman_ids: ids, at, ...gps });
            if (data._offline) {
                const l = readLocal().filter((x) => !(x.job_id === job && (!ids || ids.includes(x.craftsman_id)))); writeLocal(l); setLocal(l);
                toast.info(`Odchod ${hhmm(at)} uložen offline — hodiny se zapíší po připojení`);
            } else {
                const c = data.closed || [];
                const brk = c[0]?.break_minutes;
                toast.success(c.length === 1 ? `Odchod ${hhmm(data.at)} — ${c[0].craftsman_name}: zapsáno ${fmtH(c[0].hours)}${brk ? ` (pauza ${brk} min)` : ""}` : `Odchod ${hhmm(data.at)} — zapsáno ${c.length} řemeslníkům${brk ? ` (pauza ${brk} min)` : ""}`);
                if (c.some((x) => x.blocked)) toast.warning(c.filter((x) => x.blocked).map((x) => `${x.craftsman_name}: ${x.blocked}`).join(" "));
                load(); setHistKey((k) => k + 1); onSaved?.();
            }
        } catch (e) { toast.error(e.response?.data?.detail || "Odchod se nepodařilo zapsat"); }
        setBusy(false);
    };

    const cancel = async (p) => {
        if (!window.confirm(`Zrušit příchod ${p.name} bez zápisu hodin?`)) return;
        if (p.offline) { const l = readLocal().filter((x) => !(x.job_id === job && x.craftsman_id === p.craftsman_id)); writeLocal(l); setLocal(l); return; }
        try { await api.delete(`/attendance/${p.id}`); toast.success("Příchod zrušen"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Nelze zrušit"); }
    };

    const bigBtn = "min-h-[56px] px-6 inline-flex items-center justify-center gap-2 font-bold uppercase tracking-wider text-sm rounded-sm disabled:opacity-60";

    return (
        <div className="bg-white border border-slate-200" data-testid="attendance-panel">
            <div className="px-5 py-3 border-b-2 border-slate-900 flex items-center justify-between gap-2 flex-wrap">
                <span className="inline-flex items-center gap-2 font-display font-bold uppercase tracking-wider text-sm"><Clock className="w-4 h-4" /> Docházka na stavbě</span>
                {present.length > 0 && <span className="text-xs text-slate-500" data-testid="attendance-present-count"><Users className="w-3.5 h-3.5 inline mr-1" />Přítomno {present.length}</span>}
            </div>
            <div className="p-5 space-y-5">
                {!jobId && (
                    <select value={job} onChange={(e) => setJob(e.target.value)} data-testid="attendance-job" className="w-full min-h-[48px] px-3 py-2 border border-slate-300 bg-white text-sm rounded-sm">
                        <option value="">— vyberte zakázku —</option>
                        {(jobs || []).map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
                    </select>
                )}

                {present.length > 0 && (
                    <div className="bg-slate-900 text-white border-l-4 border-emerald-500" data-testid="attendance-open">
                        <ul className="divide-y divide-slate-700">
                            {present.map((p) => (
                                <li key={p.craftsman_id} className="px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`attendance-present-${p.craftsman_id}`}>
                                    <div className="min-w-0 basis-full sm:basis-auto sm:flex-1">
                                        <div className="font-semibold flex items-center gap-2 flex-wrap">{p.name}{p.offline && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-300"><WifiOff className="w-3 h-3" /> offline</span>}</div>
                                        <div className="text-xs text-slate-300 tabular-data">od {hhmm(p.at)} · <span className="font-display font-black text-emerald-400 text-sm" data-testid={`attendance-timer-${p.craftsman_id}`}>{elapsed(p.at)}</span>{p.gps && <MapPin className="w-3 h-3 inline ml-1 text-slate-500" />}</div>
                                    </div>
                                    <button onClick={() => checkOut([p.craftsman_id])} disabled={busy} data-testid={`attendance-checkout-${p.craftsman_id}`} className={`${bigBtn} flex-1 sm:flex-none bg-emerald-600 hover:bg-emerald-700 text-white`}>
                                        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogOut className="w-5 h-5" />} Odchod
                                    </button>
                                    <button onClick={() => cancel(p)} className="min-h-[44px] px-2 text-[10px] uppercase tracking-wider text-slate-400 hover:text-white" data-testid={`attendance-cancel-${p.craftsman_id}`}>Zrušit</button>
                                </li>
                            ))}
                        </ul>
                        {present.length > 1 && (
                            <div className="px-4 py-3 border-t border-slate-700">
                                <button onClick={() => checkOut(null)} disabled={busy} data-testid="attendance-checkout-all" className={`${bigBtn} w-full bg-white text-slate-900 hover:bg-emerald-50`}><LogOut className="w-5 h-5" /> Odchod všichni ({present.length})</button>
                            </div>
                        )}
                    </div>
                )}

                {(!solo || present.length === 0) && (
                    <div>
                        {!solo && <div className="label-eyebrow mb-2">{present.length ? "Kdo přišel později" : "Kdo je dnes na stavbě"}</div>}
                        {!solo && cards.length === 0 && <div className="text-sm text-slate-400">Nejsou žádné karty řemeslníků (Tým firmy → Řemeslníci).</div>}
                        {!solo && others.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                                {others.map((c) => {
                                    const away = elsewhere[c.id];
                                    return (
                                        <label key={c.id} data-testid={`attendance-pick-${c.id}`} className={`flex items-center gap-3 min-h-[52px] px-3 border rounded-sm select-none ${away ? "border-amber-300 bg-amber-50 cursor-not-allowed" : picked.includes(c.id) ? "border-orange-600 bg-orange-50 cursor-pointer" : "border-slate-200 bg-white cursor-pointer"}`}>
                                            <input type="checkbox" checked={picked.includes(c.id)} disabled={!!away} onChange={() => toggle(c.id)} className="w-5 h-5 accent-orange-600" />
                                            <span className="min-w-0">
                                                <span className="text-sm font-medium text-slate-900 block">{c.name}</span>
                                                {away && <span className="text-[11px] text-amber-800 block" data-testid={`attendance-elsewhere-${c.id}`}>na zakázce „{away.job_title}“ od {hhmm(away.since)}</span>}
                                            </span>
                                            {!away && c.specialization && <span className="text-xs text-slate-500 ml-auto">{c.specialization}</span>}
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                        <button onClick={checkIn} disabled={busy || (!solo && !cards.length)} data-testid="attendance-checkin" className={`${bigBtn} w-full sm:w-auto bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50`}>
                            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogIn className="w-5 h-5" />} Příchod {!solo && picked.length > 0 && `(${picked.length})`}
                        </button>
                    </div>
                )}
                <p className="text-xs text-slate-500">{solo ? "Při odchodu se vám zapíší odpracované hodiny k zakázce" : "Každý má vlastní příchod a odchod; při odchodu se mu zapíší hodiny k zakázce"}{jobTitle ? ` „${jobTitle}"` : ""}. Nad 6 h se automaticky odečte 30 min pauza. Kdo nemá odchod do 22:00, tomu se čas ukončí automaticky a záznam se označí ke kontrole.</p>
                {job && <AttendanceHistory key={histKey} jobId={job} solo={solo} onChanged={() => { load(); onSaved?.(); }} />}
            </div>
        </div>
    );
}
